import { computed } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { BookmarkState } from './bookmark.state';
import { Bookmark } from '../../models/bookmark';
import { StateFilter } from '../crud.state';
import { Indicator } from '../../models/indicator';
import { makeUniqueStringArray } from '../../utility/array.utility';
import {
  CustomGridStackWidget,
  TeaserItem,
  WidgetConfig,
} from '../../services/grid-widget-config.service';

export const filterItems = (items: Bookmark[], filter: StateFilter) => {
  return items.filter(item => {
    const titleMatch =
      item.title.toLowerCase().includes(filter.query.toLowerCase()) ||
      filterBookmarksByWidgetId(item, filter.query);
    const publishedMatch =
      filter.published === item.published || filter.published === null;
    const tagsMatch =
      !filter.tags ||
      filter.tags.length === 0 ||
      (item.tags &&
        filter.tags.some(tagId => item.tags && item.tags.includes(tagId)));

    const categoryMatch =
      !filter.category ||
      filter.category.length === 0 ||
      (!!item.category && filter.category.includes(item.category));

    const idMatch =
      !filter.ids ||
      filter.ids.length === 0 ||
      filter.ids.includes(item._id) ||
      filter.ids.includes(item._id);

    return titleMatch && publishedMatch && tagsMatch && categoryMatch && idMatch;
  });
};

export const filterBookmarksByWidgetId = (
  bookmark: Bookmark,
  id: string
): boolean => {
  return bookmark.config.some(widget => {
    return widget._id && String(widget._id).includes(id);
  });
};

export function withBookmarkSelectors() {
  return signalStoreFeature(
    {
      state: type<BookmarkState>(),
    },
    withComputed(({ items, filter }) => ({
      count: computed(() => Object.keys(items()).length),
      asArray: computed(() => Object.values(items())),
      sorted: computed(
        () =>
          Object.values(items()).sort((a, b) =>
            a.sorting < b.sorting ? -1 : 1
          ) as Bookmark[]
      ),
      filteredSorted: computed(() => {
        let filtered = Object.values(items());
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered.sort((a, b) =>
          a.sorting < b.sorting ? -1 : 1
        ) as Bookmark[];
      }),
      filteredItems: computed(() => {
        let bookmarks = Object.values(items());
        if (filter()) {
          bookmarks = filterItems(bookmarks, filter());
        }
        return bookmarks as Bookmark[];
      }),
      byLandingPage: computed(() =>
        Object.values(items()).filter(
          item => item.displayOnLandingPage === true
        )
      ),
      //test: computed(({ filteredItems }) => Object.keys(items()).length as number),
      private: computed(() => {
        const bookmarks = filterItems(Object.values(items()), filter()).filter(
          bookmark => {
            return bookmark.private && !bookmark.category;
          }
        ) as Bookmark[];

        bookmarks.sort((a, b) => a.sorting - b.sorting);

        return bookmarks;
      }),
      unsorted: computed(() => {
        const bookmarks = filterItems(Object.values(items()), filter()).filter(
          bookmark => {
            return !bookmark.category && !bookmark.private;
          }
        ) as Bookmark[];

        bookmarks.sort((a, b) => a.sorting - b.sorting);

        return bookmarks;
      }),
      groupedByCategory: computed(() => {
        const grouped = filterItems(Object.values(items()), filter()).reduce(
          (acc, bookmark) => {
            const { category } = bookmark;
            if (!category) return acc;
            if (!acc[category]) acc[category] = [];
            acc[category].push(bookmark);
            return acc;
          },
          {} as Record<string, Bookmark[]>
        );

        // Sort each group
        Object.values(grouped).forEach(group =>
          group.sort((a: Bookmark, b: Bookmark) => a.sorting - b.sorting)
        );

        return grouped;
      }),
      withGuestLinks: computed(() => {
        // Sort each group
        return Object.values(items())
          .filter(bookmark => bookmark.guestLink && bookmark.guestLink.token)
          .sort(
            (a: Bookmark, b: Bookmark) => a.sorting - b.sorting
          ) as Bookmark[];
      }),
    })),
    withMethods(store => ({
      globalSearch(query: string, tags: string[]): Bookmark[] {
        const itemsArray = Object.values(store.items());
        const tempFilter: StateFilter = { ...store.filter(), query, tags };
        return filterItems(itemsArray, tempFilter);
      },
      getByCategoryId(categoryId: string): Bookmark[] {
        return Object.values(store.items()).filter(
          bookmark => bookmark.category === categoryId
        );
      },
      getByIdOrToken(idOrToken: string): Bookmark | undefined {
        const items = store.items();
        if (items[idOrToken]) return items[idOrToken];
        return Object.values(items).filter(
          bookmark => bookmark.guestLink?.token === idOrToken
        )[0];
      },
      getIndicatorsFromBookmark(widgetConfig: WidgetConfig): string[] {
        const ids: string[] = [];
        // Single indicator
        if (widgetConfig.indicator) {
          ids.push(widgetConfig.indicator);
        }

        // Multiple indicators
        if (
          widgetConfig.indicators &&
          Array.isArray(widgetConfig.indicators) &&
          widgetConfig.indicators.length > 0
        ) {
          ids.push(...widgetConfig.indicators);
        }

        // Axis properties for scatter/bubble charts
        if (
          widgetConfig.axisProperties &&
          Array.isArray(widgetConfig.axisProperties)
        ) {
          ids.push(...widgetConfig.axisProperties);
        }

        return [...new Set(ids)]; // Remove duplicates
      },
      hasIndicatorReference(bookmark: Bookmark, indicatorId: string): boolean {
        return bookmark.config.some((widget: CustomGridStackWidget) => {
          return this.getIndicatorsFromBookmark(widget.widget).includes(
            indicatorId
          );
        });
      },
      numberOfBookmarkReferencesByIndicatorId(indicatorId: string): number {
        const items = store.items();
        let count = 0;
        Object.values(items).forEach((bookmark: Bookmark) => {
          if (this.hasIndicatorReference(bookmark, indicatorId)) count++;
        });
        return count;
      },
      numberOfBookmarkReferencesByImageId(imageId: string): number {
        const items = store.items();
        let count = 0;
        Object.values(items).forEach((bookmark: Bookmark) => {
          if (this.hasImageReference(bookmark, imageId)) count++;
        });
        return count;
      },
      getImageIdsFromWidget(widgetConfig: WidgetConfig): string[] {
        if (
          widgetConfig.teaser &&
          widgetConfig.teaser.items &&
          widgetConfig.teaser.items.length > 0
        ) {
          return [
            ...new Set(
              widgetConfig.teaser.items
                .filter((item: TeaserItem) => !!item.backgroundId)
                .map((item: TeaserItem) => item.backgroundId || '')
            ),
          ];
        }

        return [];
      },
      hasImageReference(bookmark: Bookmark, imageId: string): boolean {
        return bookmark.config.some((widget: CustomGridStackWidget) => {
          return this.getImageIdsFromWidget(widget.widget).includes(imageId);
        });
      },
    }))
  );
}
